import { getUser } from '../helpers/helperMethods';
import {
  PULSE_COLLECTOR_URL_ENV,
  PULSE_DEBUG_ENV,
  PULSE_KEEPALIVE_MAX_BYTES,
  PULSE_TRACKER_ENABLED_ENV,
} from './pulseConstants';
import {
  captureAttributionFromUrl,
  consumePendingAttribution,
  readCookieYesConsent,
  readCroVariants,
} from './pulseContext';
import { registerCroVariantsInspector } from './pulseCroVariants';
import { normalizePulseKeys } from './pulseKeyNormalization';
import { attachGa4IdsToEvents } from './ga4/ga4Ids';
import {
  createEmptyPulseEvent,
  pickPulseEnvelope,
  type PulseEvent,
  type PulseEventName,
  type PulseEventPartial,
  type PulseSource,
} from './pulseSchema';
import { createPulseEventId, takeLegacyOutboundEvents } from './pulseStore';

type Identity = {
  user_id?: string | number | null;
  email?: string | null;
  phone_number?: string | null;
  new_user_indicator?: boolean | null;
};

export type PulseGa4MirrorHook = (event: PulseEvent) => void;

let pulseGa4MirrorHook: PulseGa4MirrorHook | null = null;

/** Registered by src/analytics/ga4 at init — mirrors when Pulse sends fire. */
export const registerPulseGa4MirrorHook = (hook: PulseGa4MirrorHook | null): void => {
  pulseGa4MirrorHook = hook;
};

const mirrorBuiltEventToGa4 = (event: PulseEvent): void => {
  if (!pulseGa4MirrorHook) return;
  try {
    pulseGa4MirrorHook(event);
  } catch {
    /* GA4 must never break Pulse send pipeline */
  }
};

let initialized = false;
let identity: Identity = {};

const isPulseDebug = (): boolean =>
  String(process.env[PULSE_DEBUG_ENV] || '').toLowerCase() === 'true';

/** Tracing, off unless REACT_APP_PULSE_DEBUG=true. */
const pulseDebug = (event: string, detail: Record<string, unknown>): void => {
  if (!isPulseDebug()) return;
  try {
    // eslint-disable-next-line no-console
    console.info(`[pulse] ${event}`, detail);
  } catch (_) {}
};

const isEnabled = (): boolean =>
  String(process.env[PULSE_TRACKER_ENABLED_ENV] || '').toLowerCase() ===
  'true';

const collectorUrl = (): string | null => {
  const url = (process.env[PULSE_COLLECTOR_URL_ENV] || '').trim().replace(/\/$/, '');
  return url || null;
};

const isSuccessStatus = (status: number): boolean =>
  status === 200 || status === 202;

const safe = (fn: () => void): void => {
  try {
    fn();
  } catch (_) {
    // Tracking must never affect UX.
  }
};

const pageDefaults = () => {
  try {
    const path = window.location.pathname
      ? decodeURIComponent(window.location.pathname)
      : null;
    const href = window.location.href
      ? decodeURI(window.location.href)
      : null;
    return {
      page_url: href,
      page_path: path,
      page_id: null as string | null,
      // Only from campaign URL params (see attribution) — never document.referrer
      http_referrer: null as string | null,
    };
  } catch {
    return {
      page_url: null,
      page_path: null,
      page_id: null,
      http_referrer: null,
    };
  }
};

/** Explicit partial page fields win over window.location (e.g. checkout cart icon after redirect). */
const resolvePageFields = (
  partial: PulseEventPartial,
  fallback: ReturnType<typeof pageDefaults>
): { page_path: string | null; page_url: string | null } => {
  const page_path =
    partial.page_path !== undefined && partial.page_path !== null
      ? partial.page_path
      : fallback.page_path;

  if (partial.page_url !== undefined && partial.page_url !== null) {
    return { page_path, page_url: partial.page_url };
  }

  if (
    partial.page_path !== undefined &&
    partial.page_path !== null &&
    typeof window !== 'undefined'
  ) {
    try {
      return {
        page_path,
        page_url: decodeURI(`${window.location.origin}${partial.page_path}`),
      };
    } catch {
      return { page_path, page_url: fallback.page_url };
    }
  }

  return { page_path, page_url: fallback.page_url };
};

const resolveUserFields = () => {
  try {
    const user = getUser() || {};
    return {
      user_id: identity.user_id ?? user.id ?? null,
      email: identity.email ?? user.email ?? null,
      phone_number: identity.phone_number ?? user.phone ?? null,
      new_user_indicator: !user.has_placed_an_order,
    };
  } catch {
    return {
      user_id: identity.user_id ?? null,
      email: identity.email ?? null,
      phone_number: identity.phone_number ?? null,
      new_user_indicator: true,
    };
  }
};

const resolvePulseSource = (): PulseSource => {
  try {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767px)').matches
    ) {
      return 'mobileWeb';
    }
  } catch (_) {}
  return 'web';
};

export const buildEnvelope = (partial: PulseEventPartial): PulseEvent => {
  const base = createEmptyPulseEvent(partial.event_name);
  const consent = readCookieYesConsent();
  const userFields = resolveUserFields();
  const pageFallback = pageDefaults();
  const { page_path, page_url } = resolvePageFields(partial, pageFallback);
  const attribution = consumePendingAttribution();

  const event: PulseEvent = {
    ...base,
    ...partial,
    ...userFields,
    ...consent,
    page_path,
    page_url,
    source: resolvePulseSource(),
    currency: partial.currency || 'USD',
    event_name: partial.event_name,
    event_id:
      partial.event_id ||
      createPulseEventId(
        partial.user_id !== undefined ? partial.user_id : userFields.user_id
      ),
    event_time: partial.event_time || Math.floor(Date.now() / 1000),
    cro_variants:
      partial.cro_variants !== undefined
        ? partial.cro_variants
        : readCroVariants(),
    snapchat_tracking_id: partial.snapchat_tracking_id ?? null,
    tiktok_tracking_id: partial.tiktok_tracking_id ?? null,
  };

  if (attribution) {
    event.click_id = attribution.click_id;
    event.fbclid = attribution.fbclid;
    event.ttclid = attribution.ttclid;
    event.sccid = attribution.sccid;
    event.spclid = attribution.spclid;
    event.utm_source = attribution.utm_source;
    event.utm_medium = attribution.utm_medium;
    event.utm_campaign = attribution.utm_campaign;
    event.page_id = attribution.page_id;
    event.http_referrer = attribution.http_referrer;
  } else {
    event.click_id = partial.click_id ?? null;
    event.fbclid = partial.fbclid ?? null;
    event.ttclid = partial.ttclid ?? null;
    event.sccid = partial.sccid ?? null;
    event.spclid = partial.spclid ?? null;
    event.utm_source = partial.utm_source ?? null;
    event.utm_medium = partial.utm_medium ?? null;
    event.utm_campaign = partial.utm_campaign ?? null;
    event.page_id = null;
    event.http_referrer = null;
  }

  return pickPulseEnvelope(
    normalizePulseKeys({ ...base, ...event }) as Record<string, unknown>
  );
};

/**
 * POST body is always an array: `[{...}]` or `[{...}, ...]`.
 *
 * GA4 mirroring runs first and synchronously before the Pulse POST.
 */
const sendEvents = async (
  events: PulseEvent[],
  { mirrorToGa4 = false }: { mirrorToGa4?: boolean } = {}
): Promise<boolean> => {
  if (!events.length) return true;
  const url = collectorUrl();
  if (!url) return false;

  if (mirrorToGa4) {
    for (const event of events) {
      mirrorBuiltEventToGa4(event);
    }
  }

  const payload = attachGa4IdsToEvents(events);
  const body = JSON.stringify(payload);

  // Browsers reject keepalive requests with bodies over 64KB outright, which
  // would look like a network failure and drop the send. Above the threshold,
  // give up keepalive rather than the events.
  const useKeepalive = body.length <= PULSE_KEEPALIVE_MAX_BYTES;
  if (!useKeepalive) {
    pulseDebug('keepalive skipped (payload too large)', {
      bytes: body.length,
      events: events.length,
    });
  }

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: useKeepalive,
      credentials: 'omit',
    });
    return isSuccessStatus(res.status);
  } catch {
    return false;
  }
};

const sendEventImmediate = (event: PulseEvent): void => {
  void (async () => {
    try {
      await sendEvents([event], { mirrorToGa4: true });
    } catch {
      // Failed send drops the event.
    }
  })();
};

/**
 * Fire-and-forget track. Never throws to callers.
 * POSTs immediately as `[{ event }]`. Failed sends are dropped.
 */
export const track = (partial: PulseEventPartial): void => {
  safe(() => {
    if (!isEnabled()) return;

    let event: PulseEvent;
    try {
      event = buildEnvelope(partial);
    } catch {
      return;
    }

    sendEventImmediate(event);
  });
};

/** Same as `track` — kept for existing funnel call sites. */
export const trackImmediate = (partial: PulseEventPartial): void => {
  track(partial);
};

/**
 * One-time drain of leftover `pulse_outbound_batch` from the retired queue.
 * Scheduled after this tick so a GA4 mirror hook registered during init is set
 * before leftover events send.
 */
const flushLegacyOutboundOnce = (): void => {
  const leftover = takeLegacyOutboundEvents();
  if (!leftover.length) return;
  pulseDebug('legacy outbound flush', { events: leftover.length });
  void (async () => {
    try {
      await sendEvents(leftover, { mirrorToGa4: true });
    } catch {
      // Failed send drops leftover events (already removed from storage).
    }
  })();
};

/** Test helper — reset module flags. */
export const __resetRetryStateForTests = (): void => {
  initialized = false;
  pulseGa4MirrorHook = null;
};

export const init = (): void => {
  safe(() => {
    captureAttributionFromUrl();

    if (initialized) return;
    initialized = true;
    registerCroVariantsInspector();

    if (!isEnabled()) return;

    setTimeout(() => {
      if (!isEnabled()) return;
      flushLegacyOutboundOnce();
    }, 0);
  });
};

export const setIdentity = (next: Identity): void => {
  safe(() => {
    identity = { ...identity, ...next };
  });
};

export const clearIdentity = (): void => {
  safe(() => {
    identity = {};
  });
};

/** Typed wrappers — call sites filled in later tickets. */
export const pulse = {
  init,
  track,
  setIdentity,
  clearIdentity,
  buildEnvelope,
  view: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'view' as PulseEventName }),
  click: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'click' as PulseEventName }),
  login: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'login' as PulseEventName }),
  signup: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'signup' as PulseEventName }),
  addToCart: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'add_to_cart' as PulseEventName }),
  removeFromCart: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'remove_from_cart' as PulseEventName }),
  viewCart: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'view_cart' as PulseEventName }),
  startCheckout: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    trackImmediate({ ...extra, event_name: 'start_checkout' as PulseEventName }),
  purchase: (extra: Omit<PulseEventPartial, 'event_name'> = {}) =>
    track({ ...extra, event_name: 'purchase' as PulseEventName }),
};

export default pulse;
